import React, { useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import axios from "axios";
import {
  Badge,
  Checkbox,
  EmptyState,
  FilterBar,
  Mail01,
  PageHeader,
  Pagination,
  Radio,
  RadioGroup,
  SearchLg,
  Table,
  Tabs,
  TextInput,
} from "@opendate-app/setlist";

import { useAhoyMessageIndexContext } from "./AhoyMessageIndexContainer";
import { toastError } from "../../../shared/toastHelper";
import DateRangeEditor, { formatDateRange } from "../shared/DateRangeEditor";
import MarketingVenueSelector from "../shared/MarketingVenueSelector";
import CountBadge from "../../shared/components/CountBadge";

const qs = require("qs");

const PAGE_SIZE = 25;
const STATUS_OPTIONS = ["Sent", "Opened", "Clicked", "Failed", "Converted", "Unsubscribed", "Suppressed"].map((status) => ({ value: status, label: status }));
const STATUS_COLORS = {
  Sent: "success",
  Delivered: "success",
  Opened: "success",
  Clicked: "success",
  Converted: "success",
  Bounced: "warning",
  Failed: "error",
  Unsubscribed: "error",
  Suppressed: "error",
};

const cardStyle = {
  marginTop: 24,
  background: "var(--sl-bg-primary)",
  borderRadius: "12px",
  boxShadow: "0px 1px 2px 0px rgba(10, 13, 18, 0.05)",
  border: "1px solid var(--sl-border-secondary)",
};

const editorStyle = { minWidth: 260, padding: 4 };
const listStyle = { display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto", marginTop: 8 };
const groupStyle = { fontSize: 12, fontWeight: 600, color: "var(--sl-text-tertiary)", marginTop: 4 };
const mutedStyle = { fontSize: 14, color: "var(--sl-text-placeholder)", padding: "4px 2px" };

const flattenGroups = (data) => (
  data.flatMap((entry) => (entry.options ? entry.options.map((option) => ({ ...option, group: entry.label })) : [entry]))
);

const OptionList = ({ options, values, single, onChange, close }) => {
  const pick = (value) => {
    onChange([value]);
    close();
  };

  const toggle = (value, checked) => {
    onChange(checked ? [...values, value] : values.filter((v) => v !== value));
  };

  const item = (option) => (single ? (
    <Radio size="sm" value={option.value} label={option.label} />
  ) : (
    <Checkbox
      size="sm"
      label={option.label}
      checked={values.includes(option.value)}
      onChange={(event) => toggle(option.value, event.target.checked)}
    />
  ));

  const list = options.map((option, index) => (
    <React.Fragment key={option.value}>
      {option.group && option.group !== options[index - 1]?.group && <div style={groupStyle}>{option.group}</div>}
      {item(option)}
    </React.Fragment>
  ));

  return (
    <div style={listStyle}>
      {options.length === 0 && <div style={mutedStyle}>No options</div>}
      {single ? <RadioGroup value={values[0] ?? null} onChange={pick}>{list}</RadioGroup> : list}
    </div>
  );
};

const RemoteOptionList = ({ url, mapOption, label, onLabels, ...listProps }) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let ignore = false;
    const handle = setTimeout(() => {
      axios.get(url, { params: { name: query } }).then(({ data }) => {
        if (ignore) return;
        const next = mapOption(data);
        setOptions(next);
        onLabels(next);
      }).catch(() => {
        if (!ignore) toastError(`Could not load ${label.toLowerCase()} options, please try again`);
      });
    }, query ? 300 : 0);

    return () => {
      ignore = true;
      clearTimeout(handle);
    };
  }, [url, query]);

  return (
    <div style={editorStyle}>
      <TextInput
        size="sm"
        leadingIcon={SearchLg}
        placeholder={`Search ${label.toLowerCase()}`}
        aria-label={`Search ${label.toLowerCase()}`}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <OptionList options={options} {...listProps} />
    </div>
  );
};

const TextEditor = ({ values, onChange, close, label }) => {
  const [text, setText] = useState(values[0] || "");

  const apply = () => {
    onChange(text.trim() ? [text.trim()] : []);
    close();
  };

  return (
    <div style={editorStyle}>
      <TextInput
        size="sm"
        autoFocus
        aria-label={label}
        placeholder="Press Enter to apply"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          apply();
        }}
      />
    </div>
  );
};

const buildParams = (page, sortDir, filters) => ({
  draw: page,
  start: (page - 1) * PAGE_SIZE,
  length: PAGE_SIZE,
  search: { value: "", regex: false },
  columns: { 0: { data: "created_at", searchable: false, orderable: true, search: { value: "", regex: false } } },
  order: { 0: { column: 0, dir: sortDir } },
  ...Object.fromEntries(Object.entries(filters).filter(([key, values]) => key !== "date" && values.length > 0)),
  ...(filters.date?.length === 2 ? { date: { 0: filters.date[0], 1: filters.date[1] } } : {}),
});

const link = (path, text, newTab) => (
  path ? <a href={path} target={newTab ? "_blank" : undefined} rel={newTab ? "noreferrer" : undefined} style={{ color: "var(--sl-text-brand)" }}>{text}</a> : text
);
const statusCell = (row) => <Badge size="sm" color={STATUS_COLORS[row.status_label] || "gray"}>{row.status_label}</Badge>;

const COLUMNS = {
  email: [
    { key: "created_at", header: "Date Sent", sortable: true, render: (row) => row.sent_at || "—" },
    { key: "type", header: "Type", render: (row) => row.type },
    { key: "subject", header: "Subject", primary: true, render: (row) => link(row.subject_path, row.subject_text, true) },
    { key: "recipient", header: "Recipient", render: (row) => link(row.recipient_path, row.recipient_name) },
    { key: "status", header: "Status", render: statusCell },
  ],
  sms: [
    { key: "created_at", header: "Date Sent", sortable: true, render: (row) => row.sent_at },
    { key: "body", header: "Body", primary: true, render: (row) => row.body_text },
    { key: "recipient", header: "Recipient", render: (row) => link(row.recipient_path, row.recipient_name) },
    { key: "status", header: "Status", render: statusCell },
  ],
};

const AhoyMessageIndex = () => {
  const {
    team,
    teamMembership,
    enforceMarketingVenueAccessControl,
    csrfToken,
    emailDataSource,
    smsDataSource,
    trackablesUrl,
    subjectsUrl,
    activeTab,
    setActiveTab,
    dataTableRefreshedAt,
    refreshDataTable,
  } = useAhoyMessageIndexContext();

  const [filters, setFilters] = useState({});
  const [labels, setLabels] = useState({});
  const [page, setPage] = useState(1);
  const [sortDir, setSortDir] = useState("desc");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const isEmail = activeTab === "email";
  const trackable = filters.trackable?.[0];
  const needsTrackable = isEmail && !trackable;

  useEffect(() => {
    if (needsTrackable) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return undefined;
    }

    let ignore = false;
    setLoading(true);

    axios.get(isEmail ? emailDataSource : smsDataSource, {
      params: buildParams(page, sortDir, filters),
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "brackets" }),
    }).then(({ data }) => {
      if (ignore) return;
      setRows(data.data || []);
      setTotal(data.recordsTotal || 0);
    }).catch(() => {
      if (!ignore) toastError("Could not load deliveries, please try again");
    }).finally(() => {
      if (!ignore) setLoading(false);
    });

    return () => { ignore = true; };
  }, [activeTab, page, sortDir, filters, dataTableRefreshedAt]);

  const applyFilters = (next) => {
    setFilters(isEmail && !next.trackable?.length ? {} : next);
    setPage(1);
  };

  const handleTabChange = (tab) => {
    setFilters({});
    setPage(1);
    setActiveTab(tab);
  };

  const handleMarketingVenueChange = () => {
    unstable_batchedUpdates(() => {
      setFilters({});
      setPage(1);
      refreshDataTable();
    });
  };

  const handleSortChange = (_key, direction) => {
    setSortDir(direction);
    setPage(1);
  };

  const rememberLabels = (options) => {
    setLabels((previous) => ({ ...previous, ...Object.fromEntries(options.map((option) => [option.value, option.label])) }));
  };

  const remote = (url, mapOption, single) => (props) => (
    <RemoteOptionList {...props} url={url} mapOption={mapOption} single={single} onLabels={rememberLabels} label={props.filter.label} />
  );

  const emailFilters = [
    { key: "trackable", label: "Broadcast/Campaign", render: remote(trackablesUrl, flattenGroups, true) },
    { key: "email", label: "Email", render: (props) => <TextEditor {...props} label="Email" /> },
    { key: "subject", label: "Subject", render: remote(subjectsUrl, (data) => data.map((entry) => ({ value: entry.name, label: entry.name }))) },
    { key: "status", label: "Status", render: (props) => <div style={editorStyle}><OptionList options={STATUS_OPTIONS} single {...props} /></div> },
    { key: "date", label: "Date", render: (props) => <DateRangeEditor {...props} /> },
  ];
  const smsFilters = [{ key: "date", label: "Date", render: (props) => <DateRangeEditor {...props} /> }];
  const activeFilters = isEmail ? (trackable ? emailFilters : emailFilters.slice(0, 1)) : smsFilters;

  const summariseFilter = (filter, values) => {
    if (!values || values.length === 0) return "Any";
    if (filter.key === "date") return formatDateRange(values);
    if (values.length === 1) return labels[values[0]] ?? values[0];
    return `${values.length} selected`;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title={(
          <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
            Deliveries
            <CountBadge count={total} />
          </span>
        )}
        actions={enforceMarketingVenueAccessControl ? (
          <MarketingVenueSelector
            teamId={team.id}
            teamMembership={teamMembership}
            csrfToken={csrfToken}
            onChange={handleMarketingVenueChange}
          />
        ) : null}
      />
      <div className="overflow-hidden" style={cardStyle}>
        <div className="px-4 py-3 d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
          <Tabs
            variant="button-border"
            size="sm"
            collapseBelow={0}
            items={[{ value: "email", label: "Email" }, { value: "sms", label: "SMS" }]}
            value={activeTab}
            onChange={handleTabChange}
          />
          <FilterBar
            key={`${activeTab}-${trackable || ""}`}
            variant="plain"
            filters={activeFilters}
            values={filters}
            onChange={applyFilters}
            alwaysShow={isEmail ? ["trackable"] : ["date"]}
            formatValue={summariseFilter}
            resultCount={needsTrackable ? null : `${total.toLocaleString()} ${total === 1 ? "delivery" : "deliveries"}`}
          />
        </div>

        <div style={{ padding: "4px 8px" }}>
          <Table
            columns={COLUMNS[activeTab]}
            data={rows}
            getRowKey={(row) => row.id}
            loading={loading}
            loadingLabel="Loading deliveries"
            stackBelow={640}
            sort={{ key: "created_at", direction: sortDir }}
            onSortChange={handleSortChange}
            empty={needsTrackable ? (
              <EmptyState
                size="sm"
                icon={Mail01}
                title="Select a broadcast or campaign"
                description="Pick one from the Broadcast/Campaign filter to see its deliveries."
              />
            ) : (
              <EmptyState size="sm" icon={SearchLg} title="No deliveries found" description="Try different filters." />
            )}
          />
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-top">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AhoyMessageIndex;
